import {
  MAX_SERIES_DAYS,
  ORDER_SERIES_BUCKETS,
  ORDER_SERIES_METRICS,
  ORDER_SERIES_SPLITS,
  bucketStart,
  daysBetween,
  isSeriesDate,
  shiftDate,
  type FieldOption,
  type IsoDate,
  type OrderSeriesBucket,
  type OrderSeriesMetric,
  type OrderSeriesSplit,
} from "@estelle/domain";

/**
 * What Reports > Orders' chart is set to, and how that lives in the URL (#449).
 *
 * In the address bar rather than in component state so a view can be linked to
 * and saved: "COD against prepaid, by week, since June" is a thing someone
 * sends a colleague. The filter has its own `filter` parameter, written by the
 * shared filter builder; everything else the chart is set to is one `chart`
 * parameter, so the section's search carries one more key rather than six.
 */
export interface ChartState {
  bucket: OrderSeriesBucket;
  metric: OrderSeriesMetric;
  /** Both or neither. Absent means the last 30 days, ending today. */
  range: { from: IsoDate; to: IsoDate } | null;
  splitBy: OrderSeriesSplit | null;
  includeCancelled: boolean;
}

export const DEFAULT_CHART: ChartState = {
  bucket: "day",
  metric: "orders",
  range: null,
  splitBy: null,
  includeCancelled: false,
};

/** The days the chart opens on when the link names none. */
export const DEFAULT_DAYS = 30;

/**
 * What a range wider than `MAX_SERIES_DAYS` is told. The limit is ten years of
 * days: the API refuses a wider one and so does the link reader, and a reader
 * who typed it deserves the reason rather than a report that quietly shows
 * something else (#486).
 */
export const RANGE_TOO_WIDE = "The range is at most 10 years.";

/** What a range that ends before it starts is told. */
export const START_AFTER_END = "The start date is after the end date.";

function oneOf<T extends string>(
  value: string | undefined,
  allowed: readonly T[],
): T | undefined {
  return allowed.find((candidate) => candidate === value);
}

/**
 * Read the `chart` parameter.
 *
 * Tolerant, like the filter's decoder: a link someone truncated or edited by
 * hand opens the default chart rather than an error, and each part that does
 * not parse falls back on its own, so one bad word does not discard the rest.
 */
export function decodeChart(raw: string | undefined): ChartState {
  const state: ChartState = { ...DEFAULT_CHART };
  if (!raw) return state;

  const parts = new Map<string, string>();
  for (const pair of raw.split(",")) {
    const colon = pair.indexOf(":");
    if (colon > 0) parts.set(pair.slice(0, colon), pair.slice(colon + 1));
  }

  state.bucket = oneOf(parts.get("bucket"), ORDER_SERIES_BUCKETS) ?? "day";
  state.metric = oneOf(parts.get("metric"), ORDER_SERIES_METRICS) ?? "orders";
  state.splitBy = oneOf(parts.get("split"), ORDER_SERIES_SPLITS) ?? null;
  state.includeCancelled = parts.get("cancelled") === "1";

  const from = parts.get("from");
  const to = parts.get("to");
  if (
    isSeriesDate(from) &&
    isSeriesDate(to) &&
    from <= to &&
    daysBetween(from, to) + 1 <= MAX_SERIES_DAYS
  ) {
    state.range = { from, to };
  }
  return state;
}

/**
 * The `chart` parameter for a state, or `undefined` at the defaults — so the
 * address bar carries nothing until someone changes something.
 */
export function encodeChart(state: ChartState): string | undefined {
  const parts: string[] = [];
  if (state.bucket !== DEFAULT_CHART.bucket) {
    parts.push(`bucket:${state.bucket}`);
  }
  if (state.metric !== DEFAULT_CHART.metric) {
    parts.push(`metric:${state.metric}`);
  }
  if (state.range) {
    parts.push(`from:${state.range.from}`, `to:${state.range.to}`);
  }
  if (state.splitBy) parts.push(`split:${state.splitBy}`);
  if (state.includeCancelled) parts.push("cancelled:1");
  return parts.length === 0 ? undefined : parts.join(",");
}

/** The dates the chart draws: the link's, else the last 30 days to `today`. */
export function effectiveRange(
  state: ChartState,
  today: IsoDate,
): { from: IsoDate; to: IsoDate } {
  return state.range ?? { from: shiftDate(today, 1 - DEFAULT_DAYS), to: today };
}

export type RangePreset =
  "last-7" | "last-30" | "last-90" | "this-month" | "last-month";

export const RANGE_PRESETS: readonly { id: RangePreset; label: string }[] = [
  { id: "last-7", label: "Last 7 days" },
  { id: "last-30", label: "Last 30 days" },
  { id: "last-90", label: "Last 90 days" },
  { id: "this-month", label: "This month" },
  { id: "last-month", label: "Last month" },
];

/**
 * A preset as dates, counted back from `today` in the store's calendar.
 *
 * "This month" is the month so far — to today, not to the end of it — because
 * a chart that ends on days that have not happened draws a drop that is not
 * one.
 */
export function presetRange(
  preset: RangePreset,
  today: IsoDate,
): { from: IsoDate; to: IsoDate } {
  switch (preset) {
    case "last-7":
      return { from: shiftDate(today, -6), to: today };
    case "last-30":
      return { from: shiftDate(today, -29), to: today };
    case "last-90":
      return { from: shiftDate(today, -89), to: today };
    case "this-month":
      return { from: bucketStart(today, "month"), to: today };
    case "last-month": {
      const firstOfThis = bucketStart(today, "month");
      const lastOfPrevious = shiftDate(firstOfThis, -1);
      return { from: bucketStart(lastOfPrevious, "month"), to: lastOfPrevious };
    }
  }
}

/**
 * What the split control offers: the menu's words, and the noun a sentence
 * uses for it ("split by payment").
 */
export const SPLIT_CHOICES: readonly {
  value: OrderSeriesSplit;
  label: string;
  noun: string;
}[] = [
  { value: "payment", label: "Payment (COD or prepaid)", noun: "payment" },
  {
    value: "deliveryStatus",
    label: "Delivery status",
    noun: "delivery status",
  },
  { value: "sourceApp", label: "Channel", noun: "channel" },
  {
    value: "shippingProvinceCode",
    label: "Shipping state",
    noun: "shipping state",
  },
];

/**
 * A line's name — the field's own label for the value where it has one
 * (`cod` is "Cash on delivery"), else the value as it is. The `null` line is
 * the orders with no value for the field.
 */
export function lineLabel(
  key: string | null,
  options: readonly FieldOption[] | undefined,
  split: OrderSeriesSplit | null,
): string {
  if (key === null) {
    return split === "payment" ? "No payment recorded" : "No value";
  }
  return options?.find((option) => option.value === key)?.label ?? key;
}

/** How many series colours there are: `--series-1` to `--series-8`. */
export const SERIES_COLOURS = 8;

/**
 * A colour for each line, by what the line is rather than where it ranks.
 *
 * A value with a place in the field's own option list — COD, delivered,
 * FlexyPe — takes the slot of that place, so it keeps its colour when a filter
 * changes which lines are drawn or how they rank. A value without one (a
 * state) takes the next free slot in the order the lines arrive. The caller
 * leaves "Other" out: it is drawn in the muted ink, not in a series colour.
 */
export function lineColours(
  ids: readonly string[],
  order: readonly string[],
): string[] {
  const taken = new Set<number>();
  const slotOf = new Map<string, number>();

  for (const id of ids) {
    const place = order.indexOf(id);
    if (place >= 0 && place < SERIES_COLOURS && !taken.has(place)) {
      taken.add(place);
      slotOf.set(id, place);
    }
  }
  let next = 0;
  for (const id of ids) {
    if (slotOf.has(id)) continue;
    while (taken.has(next) && next < SERIES_COLOURS - 1) next += 1;
    taken.add(next);
    slotOf.set(id, next);
  }
  return ids.map((id) => `var(--series-${String((slotOf.get(id) ?? 0) + 1)})`);
}

/*
  Month names are spelled out here, three letters each, rather than asked of
  `Intl`: English (India) writes September as "Sept", which sits beside "Sep"
  nowhere else in the app, and a month label that reads "Sept 26" is a day to
  anyone who has not been told otherwise. The year is always four digits.
*/
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const LONG_MONTH = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "long",
  year: "numeric",
});
const WEEKDAY = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  weekday: "short",
});

function at(date: IsoDate): Date {
  return new Date(`${date}T00:00:00Z`);
}

/** `10 Mar` — a date read as the calendar prints it, in no viewer's zone. */
function dayMonth(date: IsoDate): string {
  return `${String(Number(date.slice(8, 10)))} ${MONTHS[Number(date.slice(5, 7)) - 1] ?? ""}`;
}

/** `Sep 2026`. */
function monthYear(date: IsoDate): string {
  return `${MONTHS[Number(date.slice(5, 7)) - 1] ?? ""} ${date.slice(0, 4)}`;
}

/** `10 Mar 2026`. */
function fullDay(date: IsoDate): string {
  return `${dayMonth(date)} ${date.slice(0, 4)}`;
}

/** A bucket's label under the x axis: `10 Mar`, or `Sep 2026` for a month. */
export function bucketLabel(date: IsoDate, bucket: OrderSeriesBucket): string {
  return bucket === "month" ? monthYear(date) : dayMonth(date);
}

/** The same bucket in full, for the tooltip and the table. */
export function bucketDetail(date: IsoDate, bucket: OrderSeriesBucket): string {
  if (bucket === "month") return LONG_MONTH.format(at(date));
  const day = `${WEEKDAY.format(at(date))}, ${fullDay(date)}`;
  return bucket === "week" ? `Week of ${day}` : day;
}

/**
 * The days a bar holds, as briefly as they can be said: `6–10 May 2026`,
 * `30 Mar – 5 Apr 2026`, one day in full, or both years where it crosses one.
 * For a week or month cut by the range (#486), where its first day is not the
 * bar's first day.
 */
export function spanLabel(from: IsoDate, to: IsoDate): string {
  if (from === to) return fullDay(from);
  if (from.slice(0, 7) === to.slice(0, 7)) {
    return `${String(Number(from.slice(8, 10)))}–${dayMonth(to)} ${to.slice(0, 4)}`;
  }
  if (from.slice(0, 4) === to.slice(0, 4)) {
    return `${dayMonth(from)} – ${dayMonth(to)} ${to.slice(0, 4)}`;
  }
  return `${fullDay(from)} – ${fullDay(to)}`;
}

/** `10 Mar 2026 – 9 Apr 2026`, for a card's description. */
export function rangeLabel(from: IsoDate, to: IsoDate): string {
  return from === to ? fullDay(from) : `${fullDay(from)} – ${fullDay(to)}`;
}
